/**
 * AuthUIController - Handles authentication UI interactions
 * 
 * Manages login/logout UI, profile settings modal, and account deletion.
 * Subscribes to AUTH_CHANGED events to keep UI in sync.
 */

import { MODAL_IDS } from '../constants/modals.js';
import { FAVORITES_STORAGE_KEY } from '../constants/index.js';
import { eventBus, EVENTS } from '../services/EventBus.js';
import { showToast } from './ToastController.js';

// Module state
let authService = null;
let closeUserMenuFn = null;
let closeAccountMenuFn = null;

/**
 * Initialize the AuthUIController
 * @param {Object} deps - Dependencies
 * @param {Object} deps.authService - Auth service instance
 * @param {Function} deps.closeUserMenu - Function to close user menu
 * @param {Function} deps.closeAccountMenu - Function to close account menu
 * @returns {Object} Public methods for onclick handlers
 */
export function initAuthUIController(deps) {
    authService = deps.authService;
    closeUserMenuFn = deps.closeUserMenu;
    closeAccountMenuFn = deps.closeAccountMenu;
    
    // Subscribe to auth changes to update UI
    eventBus.on(EVENTS.AUTH_CHANGED, ({ isLoggedIn, user }) => {
        updateAuthUI(isLoggedIn);
    });
    
    // Return public methods for window/onclick exposure
    return {
        updateAuthUI,
        loginWithDiscord,
        logout,
        showProfileSettings,
        closeProfileSettings,
        saveProfileSettings,
        handleAvatarSourceChange,
        previewCustomAvatar,
        updateAvatarPreview,
        showDeleteAccountModal,
        closeDeleteAccountModal,
        confirmDeleteAccount
    };
}

/**
 * Update UI based on auth state
 * @param {boolean} isLoggedIn - Whether user is logged in
 */
function updateAuthUI(isLoggedIn) {
    const accountIcon = document.getElementById('account-icon');
    const accountAvatar = document.getElementById('account-avatar');
    const accountLoggedOut = document.getElementById('account-logged-out');
    const accountLoggedIn = document.getElementById('account-logged-in');
    const accountName = document.getElementById('account-name');
    const accountEmail = document.getElementById('account-email');
    
    const currentUser = authService.getCurrentUser();
    
    if (isLoggedIn && currentUser) {
        // Use profile data with Discord fallbacks (via authService)
        const avatarUrl = authService.getAvatarUrl();
        const displayName = authService.getDisplayName();
        
        // Show avatar instead of icon
        accountIcon.style.display = 'none';
        accountAvatar.style.display = 'block';
        accountAvatar.src = avatarUrl || 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><circle cx="50" cy="50" r="50" fill="%235865F2"/><text x="50" y="65" font-size="40" text-anchor="middle" fill="white">?</text></svg>';
        
        // Show logged in menu
        accountLoggedOut.style.display = 'none';
        accountLoggedIn.style.display = 'block';
        accountName.textContent = displayName;
        accountEmail.textContent = currentUser.email || '';
        
        showToast(`Welcome, ${displayName}!`);
    } else {
        // Show icon instead of avatar
        accountIcon.style.display = 'block';
        accountAvatar.style.display = 'none';
        
        // Show logged out menu
        accountLoggedOut.style.display = 'block';
        accountLoggedIn.style.display = 'none';
    }
}

/**
 * Login with Discord OAuth
 */
async function loginWithDiscord() {
    const result = await authService.loginWithDiscord();
    if (!result.success && result.error) {
        showToast('Login failed: ' + result.error.message, true);
    }
}

/**
 * Logout
 */
async function logout() {
    const result = await authService.logout();
    if (result.success) {
        showToast('Signed out');
        if (closeUserMenuFn) closeUserMenuFn();
    } else if (result.error) {
        showToast('Logout failed', true);
    }
}

// ============= PROFILE SETTINGS =============

/**
 * Show profile settings modal
 */
async function showProfileSettings() {
    if (closeAccountMenuFn) closeAccountMenuFn();
    
    // Load profile if not cached
    let userProfile = authService.getUserProfile();
    if (!userProfile) {
        userProfile = await authService.loadUserProfile();
    }
    
    // Populate form
    const displayNameInput = document.getElementById('profile-display-name');
    const avatarUrlInput = document.getElementById('profile-avatar-url');
    const discordRadio = document.querySelector('input[name="avatar-source"][value="discord"]');
    const customRadio = document.querySelector('input[name="avatar-source"][value="custom"]');
    const hintEl = document.getElementById('profile-name-hint');
    
    const discordName = authService.getDiscordName();
    
    // Set display name (show placeholder with Discord name if empty)
    displayNameInput.value = userProfile?.display_name || '';
    displayNameInput.placeholder = discordName;
    hintEl.textContent = `Leave empty to use Discord name: "${discordName}"`;
    
    // Set avatar source
    if (userProfile?.avatar_url) {
        customRadio.checked = true;
        avatarUrlInput.value = userProfile.avatar_url;
        avatarUrlInput.style.display = 'block';
    } else {
        discordRadio.checked = true;
        avatarUrlInput.value = '';
        avatarUrlInput.style.display = 'none';
    }
    
    // Update avatar preview
    updateAvatarPreview();
    
    // Show modal
    document.getElementById(MODAL_IDS.PROFILE_SETTINGS).classList.add('active');
}

/**
 * Close profile settings modal
 */
function closeProfileSettings() {
    document.getElementById(MODAL_IDS.PROFILE_SETTINGS).classList.remove('active');
}

/**
 * Handle avatar source radio button change
 */
function handleAvatarSourceChange() {
    const isCustom = document.querySelector('input[name="avatar-source"][value="custom"]').checked;
    const avatarUrlInput = document.getElementById('profile-avatar-url');
    
    avatarUrlInput.style.display = isCustom ? 'block' : 'none';
    updateAvatarPreview();
}

/**
 * Preview custom avatar (called on input)
 */
function previewCustomAvatar() {
    updateAvatarPreview();
}

/**
 * Update avatar preview image
 */
function updateAvatarPreview() {
    const isCustom = document.querySelector('input[name="avatar-source"][value="custom"]').checked;
    const avatarUrlInput = document.getElementById('profile-avatar-url');
    const avatarImg = document.getElementById('profile-avatar-img');
    const avatarPlaceholder = document.getElementById('profile-avatar-placeholder');
    
    let avatarUrl = '';
    if (isCustom && avatarUrlInput.value.trim()) {
        avatarUrl = avatarUrlInput.value.trim();
    } else {
        avatarUrl = authService.getDiscordAvatarUrl();
    }
    
    if (avatarUrl) {
        avatarImg.src = avatarUrl;
        avatarImg.classList.add('visible');
        avatarPlaceholder.style.display = 'none';
        
        // Handle load error
        avatarImg.onerror = () => {
            avatarImg.classList.remove('visible');
            avatarPlaceholder.style.display = 'block';
        };
    } else {
        avatarImg.classList.remove('visible');
        avatarPlaceholder.style.display = 'block';
    }
}

/**
 * Save profile settings
 */
async function saveProfileSettings() {
    const displayNameInput = document.getElementById('profile-display-name');
    const avatarUrlInput = document.getElementById('profile-avatar-url');
    const isCustom = document.querySelector('input[name="avatar-source"][value="custom"]').checked;
    
    const displayName = displayNameInput.value.trim() || null;
    const avatarUrl = isCustom ? (avatarUrlInput.value.trim() || null) : null;
    
    const success = await authService.saveUserProfile(displayName, avatarUrl);
    
    if (success) {
        showToast('Profile saved!');
        closeProfileSettings();
        
        // Update UI with new profile data
        updateAuthUI(true);
    } else {
        showToast('Failed to save profile', true);
    }
}

// ============= ACCOUNT DELETION =============

/**
 * Show delete account confirmation modal
 */
function showDeleteAccountModal() {
    if (closeAccountMenuFn) closeAccountMenuFn();
    
    // Reset the input field
    const input = document.getElementById('delete-confirm-input');
    const btn = document.getElementById('delete-account-btn');
    input.value = '';
    btn.disabled = true;
    
    // Set up input validation
    input.oninput = () => {
        btn.disabled = input.value.trim() !== 'DELETE';
    };
    
    document.getElementById(MODAL_IDS.DELETE_ACCOUNT).classList.add('active');
}

/**
 * Close delete account modal
 */
function closeDeleteAccountModal() {
    document.getElementById(MODAL_IDS.DELETE_ACCOUNT).classList.remove('active');
}

/**
 * Confirm and execute account deletion
 */
async function confirmDeleteAccount() {
    const input = document.getElementById('delete-confirm-input');
    if (input.value.trim() !== 'DELETE') {
        showToast('Please type DELETE to confirm', true);
        return;
    }
    
    if (!authService.isLoggedIn()) {
        showToast('Not logged in', true);
        return;
    }
    
    showToast('Deleting account data...', false);
    
    const result = await authService.deleteAccount();
    
    // Close modal
    closeDeleteAccountModal();
    
    // Clear local storage data
    localStorage.removeItem(FAVORITES_STORAGE_KEY);
    
    if (result.success) {
        showToast('Account deleted successfully. You have been signed out.', false);
    } else {
        const errorMsg = result.errors.length > 0 
            ? result.errors.map(e => e.message).join(', ')
            : 'Unknown error';
        showToast('Account deleted with some errors: ' + errorMsg, true);
    }
}
